/**
 * Frontend RBAC — pure predicates the UI uses to hide menus, routes, tabs and
 * action buttons the current user cannot use (so a FORMATEUR, or any role, sees
 * only what it is allowed to, instead of hitting 403s / silent-fails).
 *
 * The RBAC axes (`PermissionModule`, `PermissionOperation`, `ADMIN_ROLES`) are
 * NOT hand-written here: they are GENERATED from the single source of truth
 * `rbac.yaml` into `./permissions.generated` — the same file that generates the
 * backend enums (`src/auth/permissions.rs`), so the two sides cannot drift.
 *
 * The per-user permission set is likewise never hardcoded: it is the
 * authoritative `module.operation` list shipped by the backend on `/auth/login`
 * and `/auth/me` (`User.permissions`).
 */
import type { User } from "@/types/api"
import type { PermissionModule, PermissionOperation } from "./permissions.generated"
import { ADMIN_ROLES } from "./permissions.generated"

// Ré-export pour préserver l'API publique `@/lib/permissions` (les
// consommateurs importent ces symboles depuis ce module).
export type { PermissionModule, PermissionOperation } from "./permissions.generated"
export { ADMIN_ROLES } from "./permissions.generated"

export function isAdminRole(role: string): boolean {
  // `normalizeUser` (src/services/api.ts) lowercases every role at the API
  // boundary, so the runtime value is e.g. `"admin"`, while `ADMIN_ROLES`
  // mirrors the backend codes in uppercase. Compare case-insensitively so the
  // predicate holds regardless of which convention the caller passes.
  return (ADMIN_ROLES as readonly string[]).includes(role.toUpperCase())
}

/**
 * Does the user hold `<module>.<operation>` ? Admin roles always do. Everyone
 * else is checked against the backend-provided permission list (never a local
 * per-role table). VIEWER-style read roles naturally hold only `*.read`.
 */
export function userCan(
  user: User,
  module: PermissionModule,
  operation: PermissionOperation,
): boolean {
  if (isAdminRole(user.role)) {
    return true
  }
  // Une session qui n'a pas (encore) livré sa liste ne donne aucun droit,
  // plutôt que de faire planter le premier rendu (`/demo`).
  return (user.permissions ?? []).includes(`${module}.${operation}`)
}

/** Can the user open/read `module` at all — its `read` operation. */
export function userCanRead(user: User, module: PermissionModule): boolean {
  return userCan(user, module, "read")
}
